import React, { useContext, useEffect, useMemo, useState } from "react";
import "./VoyahTechnicalSpecs.css";

import { LanguageContext } from "../../../../context/LanguageContext";
import { getPageContent } from "../../../../api/publicContent.api";
import {
  buildHeroValueMap,
  buildMergedSpecItems,
} from "./voyahTechnicalSpecs.utils";

const SPEC_KEYS = ["1", "2", "3", "4", "5"];
const STORAGE_KEYS = ["1", "2"];

const VoyahTechnicalSpecsI18n = ({
  page,
  variant,
  i18nPrefix,
  tsImage,
  highlightKeys = ["1", "2", "3"],
}) => {
  const { t, currentLang } = useContext(LanguageContext);

  const [hero, setHero] = useState(null);
  const [apiSpecs, setApiSpecs] = useState(null);

  useEffect(() => {
    const loadSpecsData = async () => {
      try {
        const response = await getPageContent(page, currentLang);
        setHero(response.sections?.hero || null);
        setApiSpecs(response.sections?.technical_specs || null);
      } catch (err) {
        console.error(`Failed to load ${page} technical specs data`, err);
        setHero(null);
        setApiSpecs(null);
      }
    };

    loadSpecsData();
  }, [page, currentLang]);

  const heroMap = useMemo(() => buildHeroValueMap(hero), [hero]);

  const overallItems = useMemo(
    () =>
      buildMergedSpecItems({
        page,
        section: "overall",
        keys: SPEC_KEYS,
        t,
        i18nPrefix,
        hero,
        heroMap,
        apiSpecs,
      }),
    [currentLang, page, t, i18nPrefix, hero, heroMap, apiSpecs]
  );

  const storageItems = useMemo(
    () =>
      buildMergedSpecItems({
        page,
        section: "storage",
        keys: STORAGE_KEYS,
        t,
        i18nPrefix,
        hero,
        heroMap,
        apiSpecs,
      }),
    [currentLang, page, t, i18nPrefix, hero, heroMap, apiSpecs]
  );

  const highlights = highlightKeys
    .map((key) => ({
      title: t(`${i18nPrefix}.technicalSpecs.highlights.${key}.title`),
      description: t(
        `${i18nPrefix}.technicalSpecs.highlights.${key}.description`
      ),
    }))
    .filter((item) => item.title && !item.title.startsWith("voyah"));

  const renderSpecList = (items) => (
    <ul className="voyah-spec-list">
      {items.map((item, index) => (
        <li key={index}>
          <span className="voyah-spec-num">{index + 1}</span>
          {item.label}
          {item.value ? (
            <span className="voyah-spec-val">{item.value}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );

  if (overallItems.length === 0) return null;

  return (
    <section className={`voyah-technical-specs voyah-technical-specs--${variant}`}>
      <h2 className="voyah-tech-title">
        {t(`${i18nPrefix}.technicalSpecs.title`)}
      </h2>

      <div className="voyah-spec-row">
        <div className="voyah-spec-left">
          <span className="voyah-spec-badge">
            {t(`${i18nPrefix}.technicalSpecs.badgeOverall`)}
          </span>
          {renderSpecList(overallItems)}
        </div>

        {tsImage ? (
          <div className="voyah-spec-right">
            <img
              src={tsImage}
              alt={t(`${i18nPrefix}.technicalSpecs.badgeOverall`)}
            />
          </div>
        ) : null}
      </div>

      {storageItems.length > 0 && (
        <>
          <div className="voyah-spec-divider" />

          <div className="voyah-spec-row">
            <div className="voyah-spec-left">
              <span className="voyah-spec-badge">
                {t(`${i18nPrefix}.technicalSpecs.badgeStorage`)}
              </span>
              {renderSpecList(storageItems)}
            </div>

            {tsImage ? (
              <div className="voyah-spec-right">
                <img
                  src={tsImage}
                  alt={t(`${i18nPrefix}.technicalSpecs.badgeStorage`)}
                />
              </div>
            ) : null}
          </div>
        </>
      )}

      {highlights.length > 0 && (
        <>
          <div className="voyah-spec-divider" />

          <ul className="voyah-spec-highlights">
            {highlights.map((item, index) => (
              <li key={index} className="voyah-spec-highlight">
                <h3 className="voyah-spec-highlight-title">{item.title}</h3>
                {item.description ? (
                  <p className="voyah-spec-highlight-text">{item.description}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

export default VoyahTechnicalSpecsI18n;
